import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tag, ChevronDown, ChevronUp, Loader2, X, Ticket } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { formatCurrency } from '@/utils/formatters';
import { cn } from '@/lib/utils';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { httpsUrl } from '@/lib/safeUrl';

interface Coupon {
  code: string;
  description?: string;
  discount_type?: string;
  discount_value?: number;
  min_order?: number;
  max_discount?: number;
  /** Empty means the coupon works on every service. */
  applicable_services?: string[];
}

interface CouponSectionProps {
  couponCode: string;
  onCodeChange: (v: string) => void;
  /** Called with a code when a listed coupon is picked, bare for the field. */
  onApply: (code?: string) => void;
  onRemove: () => void;
  applied: boolean;
  discount: number;
  validating: boolean;
  availableCoupons: Coupon[];
  /** Stack the controls so the block fits a narrow column, e.g. a summary card. */
  compact?: boolean;
  /**
   * Keep the field behind a quiet trigger. An open coupon box sends people who
   * have no code off to hunt for one, and a share of them never come back.
   */
  collapsible?: boolean;
  /** The entered code is reserved for the app: show why, and where to get it. */
  appOnly?: boolean;
}

export function CouponSection({
  couponCode,
  onCodeChange,
  onApply,
  onRemove,
  applied,
  discount,
  validating,
  availableCoupons,
  compact = false,
  collapsible = false,
  appOnly = false,
}: CouponSectionProps) {
  const { user_app_android_url } = useSiteConfig();
  const appUrl = httpsUrl(user_app_android_url) ?? '';
  const [showCoupons, setShowCoupons] = useState(false);
  const [open, setOpen] = useState(!collapsible);

  if (!open && !applied) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-primary/80"
      >
        <Tag className="size-3.5" />
        Have a coupon?
      </button>
    );
  }

  return (
    <div className={compact ? 'space-y-2.5' : 'space-y-3'}>
      {!compact && (
        <Label className="flex items-center gap-2">
          <Tag className="size-4 text-primary" />
          Coupon Code
        </Label>
      )}

      {/* Applied coupon badge */}
      {applied && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            'flex items-center justify-between gap-2 rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30',
            compact ? 'p-2.5' : 'p-3',
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Badge className="bg-green-600 text-white hover:bg-green-600">
              {couponCode}
            </Badge>
            <span className="truncate text-sm font-medium text-green-700 dark:text-green-400">
              {formatCurrency(discount)} off
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onRemove}
            className="shrink-0 text-muted-foreground hover:text-destructive"
          >
            <X className="size-4" />
            {!compact && 'Remove'}
          </Button>
        </motion.div>
      )}

      {appOnly && !applied && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
          <span className="text-amber-900 dark:text-amber-200">
            This coupon works only in the Recovry app.
          </span>
          {appUrl && (
            <Button type="button" size="sm" variant="outline" asChild>
              <a href={appUrl} target="_blank" rel="noopener noreferrer">
                Get the app
              </a>
            </Button>
          )}
        </div>
      )}

      {/* Input + Apply button */}
      {!applied && (
        <div className="flex gap-2">
          <Input
            placeholder={compact ? 'Coupon code' : 'Enter coupon code'}
            value={couponCode}
            onChange={(e) => onCodeChange(e.target.value.toUpperCase())}
            className="min-w-0 flex-1 uppercase"
            disabled={validating}
          />
          <Button
            type="button"
            onClick={() => onApply()}
            disabled={!couponCode.trim() || validating}
            /* Must not compete with the page's real call to action. */
            variant={compact ? 'outline' : 'default'}
            className="shrink-0"
          >
            {validating ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Validating
              </>
            ) : (
              'Apply'
            )}
          </Button>
        </div>
      )}

      {/* Available coupons expandable */}
      {!applied && availableCoupons.length > 0 && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowCoupons(!showCoupons)}
            className="flex items-center gap-1.5 text-xs font-medium text-primary transition-colors hover:text-primary/80"
          >
            <Ticket className="size-3.5" />
            {showCoupons ? 'Hide' : 'View'} available coupons ({availableCoupons.length})
            {showCoupons ? (
              <ChevronUp className="size-3.5" />
            ) : (
              <ChevronDown className="size-3.5" />
            )}
          </button>

          <AnimatePresence>
            {showCoupons && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-2">
                  {availableCoupons.map((coupon) => (
                    <div
                      key={coupon.code}
                      className={cn(
                        'rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-3',
                        compact
                          ? 'space-y-2'
                          : 'flex items-center justify-between',
                      )}
                    >
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">
                            {coupon.code}
                          </Badge>
                        </div>
                        {coupon.description && (
                          <p className="text-xs text-muted-foreground">
                            {coupon.description}
                          </p>
                        )}
                        {coupon.applicable_services && coupon.applicable_services.length > 0 && (
                          <p className="text-[11px] text-muted-foreground/80">
                            Valid on selected services only
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onApply(coupon.code)}
                        disabled={validating}
                        className={compact ? 'w-full' : undefined}
                      >
                        Apply
                      </Button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
